import { useMemo, useState } from "react";
import { Calendar, User } from "lucide-react";
import {
  folhaPagamentoService,
  profissionalService,
} from "../../services/apiService";
import { useQuery } from "../../hooks/useQuery";
import { useNotification } from "../../hooks/useNotification";
import PageContainer from "../../components/PageContainer";
import {
  FormField,
  SelectInput,
  TextInput,
  cardClass,
  tableBodyClass,
  tableWrapperClass,
} from "../../components/ui";
import { formatarData } from "../../utils/FormatarData";
import {
  formatCurrencyMoeda,
  formatCurrencyPorcentagem,
} from "../../utils/FormatarValores";
import {
  competenciaParaInputMes,
  getCompetenciaAtual,
  inputMesParaCompetencia,
} from "../../utils/FormatarCompetencia";

const ResumoProfissional = () => {
  const { showError } = useNotification();
  const [competencia, setCompetencia] = useState(getCompetenciaAtual());
  const [profissionalId, setProfissionalId] = useState("");

  const { data: profissionais = [] } = useQuery(
    ["profissionais"],
    () => profissionalService.listarProfissionais(),
    {
      onError: (err) => {
        showError(err.message || "Erro ao carregar profissionais");
      },
    }
  );

  const { data, isLoading } = useQuery(
    ["resumo-profissional", competencia, profissionalId],
    () =>
      folhaPagamentoService.listarFolhaPagamentoMensal(
        competencia,
        profissionalId
      ),
    {
      enabled: Boolean(competencia),
      onError: (err) => {
        showError(err.message || "Erro ao carregar resumo profissional");
      },
    }
  );

  const resumoProfissionais = useMemo(() => {
    return Array.isArray(data) ? data : [];
  }, [data]);

  const profissionaisList = useMemo(() => {
    return Array.isArray(profissionais) ? profissionais : [];
  }, [profissionais]);

  const handleCompetenciaChange = (e) => {
    const value = inputMesParaCompetencia(e.target.value);
    if (!value) {
      return;
    }
    setCompetencia(value);
  };

  return (
    <PageContainer className="p-6 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Resumo Profissional</h1>
        <p className="text-sm text-gray-500">
          Relatório mensal por profissional com valores de folha de pagamento.
        </p>
      </div>

      <div className={`${cardClass} p-4 md:p-5`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <FormField label="Competência" htmlFor="resumo-competencia">
            <div className="relative">
              <Calendar
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <TextInput
                id="resumo-competencia"
                type="month"
                value={competenciaParaInputMes(competencia)}
                onChange={handleCompetenciaChange}
                className="pl-10 pr-3"
              />
            </div>
          </FormField>

          <FormField label="Profissional" htmlFor="resumo-profissional">
            <div className="relative">
              <User
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <SelectInput
                id="resumo-profissional"
                value={profissionalId}
                onChange={(e) => setProfissionalId(e.target.value)}
                className="pl-10 pr-3"
              >
                <option value="">Todos os profissionais</option>
                {profissionaisList.map((profissional) => (
                  <option key={profissional.id} value={profissional.id}>
                    {profissional.nome}
                  </option>
                ))}
              </SelectInput>
            </div>
          </FormField>

        </div>
      </div>

      {/* Agrupamento por profissional, tabela por profissional, cabeçalho colorido, especialidades, diferença, meta % */}
      <div className="space-y-10">
        {(() => {
          if (isLoading) {
            return (
              <div className={`${cardClass} p-10 text-center text-gray-500`}>Carregando dados...</div>
            );
          }
          if (resumoProfissionais.length === 0) {
            return (
              <div className={`${cardClass} p-10 text-center text-gray-500`}>Nenhum registro encontrado para os filtros selecionados.</div>
            );
          }
          // Cores para cabeçalho
          const cores = [
            "bg-green-200 text-green-900 border-green-400",
            "bg-blue-200 text-blue-900 border-blue-400",
            "bg-yellow-200 text-yellow-900 border-yellow-400",
            "bg-purple-200 text-purple-900 border-purple-400",
            "bg-pink-200 text-pink-900 border-pink-400",
            "bg-orange-200 text-orange-900 border-orange-400",
          ];
          return resumoProfissionais.map((resumo, idx) => {
            const nomeProfissional =
              resumo?.profissional?.nome ||
              resumo?.profissionalNome ||
              "Profissional";
            const especialidades = Array.isArray(resumo?.especialidadeBonus)
              ? resumo.especialidadeBonus
              : [];
            if (especialidades.length === 0) {
              return null;
            }
            const especialidadesUnicas = Array.from(
              new Set(
                especialidades
                  .map((especialidade) => especialidade?.especialidade?.nome)
                  .filter(Boolean)
              )
            );
            const linhasTabela = especialidades;
            const rowSpanTotais = linhasTabela.length;
            const diferencaValor = Number(resumo.diferenca) || 0;

            return (
              <div key={`${nomeProfissional}-${idx}`} className={`${cardClass} overflow-hidden`}>
                <div className={`p-4 rounded-t-xl border-b flex flex-col md:flex-row md:items-center md:justify-between ${cores[idx % cores.length]}`}> 
                  <div className="text-xl font-bold flex items-center gap-2">
                    <User className="inline-block" />
                    {nomeProfissional}
                  </div>
                  {especialidadesUnicas.length > 0 && (
                    <div className="mt-2 md:mt-0 text-sm font-semibold flex flex-wrap gap-2">
                      {especialidadesUnicas.map((esp) => (
                        <span key={esp} className="bg-white/60 border px-2 py-1 rounded text-xs border-current">{esp}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className={tableWrapperClass}>
                  <table className="min-w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Especialidade</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Período</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Meta (%)</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Valor Base</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Salário por Especialidade</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Salário Total</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Relatório Pagamento</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Diferença</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">% Caixa</th>
                      </tr>
                    </thead>
                    <tbody className={tableBodyClass}>
                      {linhasTabela.map((item, indexLinha) => {
                        const dataInicial =
                          item?.dataInicialPrestacaoServico ||
                          resumo?.dataInicialPrestacaoServico;
                        const dataFinal =
                          item?.dataFinalPrestacaoServico ||
                          resumo?.dataFinalPrestacaoServico;
                        const possuiPeriodo =
                          dataInicial || dataFinal;

                        return (
                        <tr key={item.id || `${nomeProfissional}-${indexLinha}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-700">{item?.especialidade?.nome || "-"}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {possuiPeriodo
                              ? `${formatarData(dataInicial)} - ${formatarData(dataFinal)}`
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                            {item?.meta === null || item?.meta === undefined
                              ? "-"
                              : formatCurrencyPorcentagem(Number(item.meta))}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">{formatCurrencyMoeda(item?.valorBase)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap font-semibold">
                            {formatCurrencyMoeda(item?.valorLiquidoPorEspecialidade)}
                          </td>
                          {indexLinha === 0 && (
                            <>
                              <td
                                rowSpan={rowSpanTotais}
                                className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap align-middle font-semibold"
                              >
                                {formatCurrencyMoeda(resumo?.valorLiquido)}
                              </td>
                              <td
                                rowSpan={rowSpanTotais}
                                className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap align-middle"
                              >
                                {formatCurrencyMoeda(resumo?.pagamentoRecebido)}
                              </td>
                              <td
                                rowSpan={rowSpanTotais}
                                className={`px-4 py-3 text-sm text-right whitespace-nowrap font-semibold align-middle ${
                                  diferencaValor > 0
                                    ? "text-green-700"
                                    : diferencaValor < 0
                                    ? "text-red-700"
                                    : "text-gray-700"
                                }`}
                              >
                                {formatCurrencyMoeda(diferencaValor)}
                              </td>
                              <td
                                rowSpan={rowSpanTotais}
                                className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap align-middle"
                              >
                                {resumo?.porcentagemCaixa === null ||
                                resumo?.porcentagemCaixa === undefined
                                  ? "-"
                                  : formatCurrencyPorcentagem(
                                      resumo.porcentagemCaixa
                                    )}
                              </td>
                            </>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          });
        })()}
      </div>
    </PageContainer>
  );
};

export default ResumoProfissional;
